import type { SimulationCuePlaylistContext } from '@/helpers/simulation-cue-engine.helper';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { useLocalMediaPreviewSlideStore } from '@/state/stores/use-local-media-preview-slide-store';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';

function mediaIdFromItemId(itemId: string): string | undefined {
  if (!itemId.startsWith('media:')) {
    return undefined;
  }
  return itemId.slice('media:'.length);
}

/**
 * @deprecated Cue-row meta.slideIndex is not the resume source (use live store / mediaSequenceIndex).
 * Kept for tests that still assert row projection.
 */
export function resolveCueRowSlideIndex(
  itemId: string | undefined,
  options?: {
    rows?: SimulationCueSheetRow[];
    playlistContext?: SimulationCuePlaylistContext;
    cueItemId?: string;
  },
): number | undefined {
  if (!itemId?.startsWith('media:')) {
    return undefined;
  }
  const rows = options?.rows ?? useFlowControlSimulationCueStore.getState().rows;
  if (!rows.length) {
    return undefined;
  }

  const withSlide = rows.find((row) => row.itemId === itemId && row.slideIndex != null && row.slideIndex > 0);
  return withSlide?.slideIndex != null && withSlide.slideIndex > 0 ? withSlide.slideIndex : undefined;
}

/**
 * Slide to apply on take/jump.
 * 1. Recent operator pick (Next/Current live nav, or item PreviewModal)
 * 2. Last known live index for that media (seeded from `mediaSequenceIndex` WS)
 * 3. Local asset preview / simulation nav
 * Cue-row meta.slideIndex is intentionally ignored (stale after sheet rebuild).
 */
export function resolveStartSlideIndexForTake(
  itemId: string | undefined,
  options?: {
    rows?: SimulationCueSheetRow[];
    playlistContext?: SimulationCuePlaylistContext;
    cueItemId?: string;
  },
): number | undefined {
  // Call-site options retained for API compatibility; cue-row meta is intentionally ignored.
  void options;
  if (!itemId?.startsWith('media:')) {
    return undefined;
  }

  const liveStore = useLiveSequenceIndexStore.getState();
  const live = liveStore.indexByItemId[itemId];
  if (liveStore.isRecentlyOptimistic(itemId) && typeof live === 'number' && live > 0) {
    return live;
  }

  const mediaId = mediaIdFromItemId(itemId);
  const localStore = useLocalMediaPreviewSlideStore.getState();
  const local = mediaId ? localStore.indexByMediaId[mediaId] : undefined;
  // Item PreviewModal (Control list) uses local nav — that operator pick must beat durable live.
  if (mediaId && localStore.isRecentlyOperatorPicked(mediaId) && typeof local === 'number' && local > 0) {
    return local;
  }

  if (typeof live === 'number' && live > 0) {
    return live;
  }

  if (typeof local === 'number' && local > 0) {
    return local;
  }

  const simulation = useFlowControlSimulationCueStore.getState().slideIndexByItemId[itemId];
  if (typeof simulation === 'number' && simulation > 0) {
    return simulation;
  }

  return undefined;
}

/** Seed Current badge immediately on jump/take from the resolved start slide. */
export function seedLiveSlideIndexForTake(itemId: string | undefined, startSlideIndex: number | undefined): void {
  if (!itemId?.startsWith('media:') || startSlideIndex == null || startSlideIndex < 1) {
    return;
  }
  useLiveSequenceIndexStore.getState().setIndex(itemId, startSlideIndex, { optimistic: true });
}

/**
 * Seed the item-preview (local) slide store from durable resume.
 * Live store is filled from perform-control `mediaSequenceIndex` (DB source of truth).
 * Call when opening PreviewModal / mounting a local-scope presentation layer.
 */
export function seedLocalMediaPreviewFromDurableResume(itemId: string | undefined): number | undefined {
  if (!itemId?.startsWith('media:')) {
    return undefined;
  }
  const mediaId = mediaIdFromItemId(itemId);
  if (!mediaId) {
    return undefined;
  }
  const live = useLiveSequenceIndexStore.getState().indexByItemId[itemId];
  if (!(typeof live === 'number' && live > 0)) {
    return undefined;
  }
  // Not an operator pick — just hydrate the modal to the DB resume index.
  useLocalMediaPreviewSlideStore.getState().setSlideIndex(mediaId, live);
  return live;
}

/** Seed live store from control-preview layers stamped with mediaSequenceIndex. */
export function seedLiveSlideIndexFromPreviewLayers(
  itemId: string | undefined,
  playerSyncData: Record<string, unknown> | undefined,
): number | undefined {
  if (!itemId?.startsWith('media:') || !playerSyncData) {
    return undefined;
  }
  const layers = playerSyncData.layers;
  if (!Array.isArray(layers)) {
    return undefined;
  }
  return seedLiveSlideIndexFromLayerCurrentIndex(
    itemId,
    layers as { data?: Record<string, unknown>; itemId?: string }[],
  );
}

/**
 * Align Next preview chrome with BE look-ahead stamps (`mediaSequenceIndex`).
 * Overwrites a stale live-store value (e.g. former item A's index leaked onto C) unless the
 * operator just navigated this deck.
 */
export function seedLiveSlideIndexFromLayerCurrentIndex(
  itemId: string | undefined,
  layers: { data?: Record<string, unknown>; itemId?: string }[] | undefined,
): number | undefined {
  if (!itemId?.startsWith('media:') || !layers?.length) {
    return undefined;
  }
  for (const layer of layers) {
    if (layer.itemId && layer.itemId !== itemId) {
      continue;
    }
    const raw = layer.data?.currentIndex;
    const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : typeof raw === 'number' ? raw : Number.NaN;
    if (Number.isFinite(n) && n > 0) {
      const liveStore = useLiveSequenceIndexStore.getState();
      if (!liveStore.isRecentlyOptimistic(itemId)) {
        liveStore.setIndex(itemId, n);
      }
      return n;
    }
  }
  return undefined;
}
